import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  createOffer,
  updateOffer,
  getOffer,
  getOffers,
  updateOfferStatus,
  bulkCreateOffer,
  getOfferScheduleLogs,
  getOfferSyncLogs,
  getOfferTemplates,
  createOfferTemplate,
  type CreateTemplatePayload,
} from '../services/api';
import type { OfferFormData, Status, BulkOfferPayload } from '../types';
import { handleErrorNotification, handleSuccessNotification } from '@core/utils/notifications';

// Query keys
export const OFFER_QUERY_KEYS = {
  all: ['offers'],
  lists: () => [...OFFER_QUERY_KEYS.all, 'list'],
  list: (filters: Record<string, any>) => [...OFFER_QUERY_KEYS.lists(), filters],
  details: () => [...OFFER_QUERY_KEYS.all, 'detail'],
  detail: (id: number) => [...OFFER_QUERY_KEYS.details(), id],
  scheduleLogs: (offerId: number | string) => [...OFFER_QUERY_KEYS.details(), 'scheduleLogs', offerId],
  syncLogs: (offerId: number | string) => [...OFFER_QUERY_KEYS.details(), 'syncLogs', offerId],
  templates: () => [...OFFER_QUERY_KEYS.all, 'templates'],
};

/**
 * Hook for fetching offers list with pagination and filters
 */
export const useOffers = (params?: { page?: number; limit?: number; search?: string }) => {
  return useQuery({
    queryKey: OFFER_QUERY_KEYS.list(params || {}),
    queryFn: () => getOffers(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)
    placeholderData: (prev) => prev,
  });
};

/**
 * Hook for fetching a single offer by ID
 */
export const useOffer = (id: number) => {
  return useQuery({
    queryKey: OFFER_QUERY_KEYS.detail(id),
    queryFn: () => getOffer(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.data,
  });
};

/**
 * Hook for fetching offer schedule logs
 */
export const useOfferScheduleLogs = (offerId?: number | string) => {
  return useQuery({
    queryKey: OFFER_QUERY_KEYS.scheduleLogs(offerId),
    queryFn: () => getOfferScheduleLogs(offerId),
    enabled: !!offerId,
    staleTime: 2 * 60 * 1000,
    select: (resp) => resp.data,
  });
};

/**
 * Hook for fetching offer sync logs
 */
export const useOfferSyncLogs = (offerId?: number | string) => {
  return useQuery({
    queryKey: OFFER_QUERY_KEYS.syncLogs(offerId),
    queryFn: () => getOfferSyncLogs(offerId),
    enabled: !!offerId,
    staleTime: 2 * 60 * 1000,
    select: (resp) => resp.data,
  });
};

/**
 * Hook for bulk creating offers
 */
export const useBulkCreateOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<BulkOfferPayload, 'game_id'>) => bulkCreateOffer(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: OFFER_QUERY_KEYS.all });
      handleSuccessNotification(response, 'Bulk offers created successfully');
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to create bulk offers');
    },
  });
};

/**
 * Hook for creating a new offer
 */
export const useCreateOffer = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: OfferFormData) => createOffer(data),
    onSuccess: (response) => {
      // Invalidate offers list to refresh the cache
      queryClient.invalidateQueries({ queryKey: OFFER_QUERY_KEYS.all });
      handleSuccessNotification(response, 'Offer created successfully');
      // Navigate to the created offer's detail page
      navigate(`/offers`);
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to create offer');
    },
  });
};

/**
 * Hook for updating an existing offer
 */
export const useUpdateOffer = (id: number) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: OfferFormData) => updateOffer(id, data),
    onSuccess: (response) => {
      // Invalidate all offers queries to refresh the cache
      queryClient.invalidateQueries({ queryKey: OFFER_QUERY_KEYS.all });
      handleSuccessNotification(response, 'Offer updated successfully');
      // Navigate back to preserve filters
      navigate(-1);
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to update offer');
    },
  });
};

/**
 * Hook for toggling offer status (Active/Inactive)
 */
export const useToggleOfferStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: Status }) => updateOfferStatus(id, status),
    onSuccess: (response) => {
      handleSuccessNotification(response, 'Offer status updated successfully');
      // Invalidate all offers queries to refresh the cache
      queryClient.invalidateQueries({ queryKey: OFFER_QUERY_KEYS.all });
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to update offer status');
    },
  });
};

/**
 * Hook for fetching offer templates
 */
export const useOfferTemplates = () => {
  return useQuery({
    queryKey: OFFER_QUERY_KEYS.templates(),
    queryFn: () => getOfferTemplates(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (resp) => resp.data,
  });
};

/**
 * Hook for creating offer template
 */
export const useCreateOfferTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplatePayload) => createOfferTemplate(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: OFFER_QUERY_KEYS.templates() });
      handleSuccessNotification(response, 'Template created successfully');
    },
    onError: (error) => {
      handleErrorNotification(error, 'Failed to create template');
    },
  });
};
